import React from 'react';

import type { AiChatReference, Insight } from '~/components/AiChat/types';
import { useInsightsData } from '~/components/ClaimPage/CliveApp/CliveInsights/contexts/InsightsDataProvider';
import { ComplexityBadge } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/ComplexityBadge';
import { InsightCardBody } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/InsightCardBody';
import Button from '~/components/core/Atomic/Buttons/Button';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import { Text } from '~/components/core/TextComponents';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import cn from '~/Utils/cn';

interface InsightHistoryCardProps {
  version: Insight;
  references: Record<string, AiChatReference>;
}

/**
 * One older version of an insight, inside the history timeline.
 *
 * Collapsed it shows when the version was generated, its complexity, and its summary; expanding
 * reveals the same body the latest card renders. Dismiss acts on this exact version — under the
 * append-only model an id identifies a version, so dismissing here can never hide a newer one.
 */
export const InsightHistoryCard: React.FC<InsightHistoryCardProps> = ({ version, references }) => {
  const { dismissInsight } = useInsightsData();
  const isDismissed = Boolean(version.is_dismissed);

  const complexity =
    version.insight_key === 'complexity'
      ? ((version.insight_metadata as Record<string, unknown>)?.complexity as string)
      : null;

  // `processInsightsResponse` blanks sub_title for some keys (claim_action_plan among them), and a
  // node carrying nothing but a timestamp gives the reader no way to pick a version.
  const subtitle = version.sub_title || version.title;

  // Rendered as one node rather than the wrapper's separate title/subtitle/actions slots: those
  // slots left-indent the subtitle, stretch the actions into their own tall row, and never apply
  // their container padding — which together bloated the card. Owning the layout keeps it compact
  // and aligned. Dismiss stops propagation so it doesn't also toggle the version open.
  const summary = (
    <div className="flex w-full items-start justify-between gap-16">
      <span className="flex flex-col gap-8">
        <span className="flex flex-wrap items-center gap-8">
          {version.created_at ? (
            <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {serverDateTimeToLocal(version.created_at)}
            </Text>
          ) : null}
          {complexity ? <ComplexityBadge complexity={complexity} /> : null}
        </span>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.PRIMARY}>
          {subtitle}
        </Text>
      </span>
      <span onClick={(event) => event.stopPropagation()} className="shrink-0">
        <Button
          variant="text"
          className={cn('font-medium normal-case text-pink-600 hover:bg-transparent disabled:text-pink-600', {
            'opacity-50': isDismissed,
          })}
          onClick={() => dismissInsight(version)}
          disabled={isDismissed}
        >
          {isDismissed ? 'Dismissed' : 'Dismiss'}
        </Button>
      </span>
    </div>
  );

  return (
    <CollapsibleWrapper
      title={summary}
      chevronContainerClassName="hidden"
      className="flex flex-col gap-8 rounded-lg border-1 border-solid border-grey-200 p-16"
    >
      <InsightCardBody insight={version} references={references} />
    </CollapsibleWrapper>
  );
};
